import {
  BellRing,
  ChevronRight,
  HardDrive,
  Link2,
  Loader2,
  Menu,
  Network,
  RefreshCw,
  Upload,
  Users,
} from 'lucide-react'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'

import { ToastHistorySheet } from '@/components/shared/toast-history/toast-history-sheet'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { checkForUpdate } from '@/lib/pwa-update'
import { PATHS } from '@/routes/paths'
import { useAppStore } from '@/store/useAppStore'
import { selectUnreadToastCount, useToastHistoryStore } from '@/store/useToastHistoryStore'

export const HamburgerMenu = () => {
  const navigate = useNavigate()
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const showDebugOverlay = useAppStore((state) => state.showDebugOverlay)
  const toggleDebugOverlay = useAppStore((state) => state.toggleDebugOverlay)
  const [open, setOpen] = useState(false)
  const [toastHistoryOpen, setToastHistoryOpen] = useState(false)
  const unreadToastCount = useToastHistoryStore(selectUnreadToastCount)
  const [updateStatus, setUpdateStatus] = useState<'idle' | 'checking' | 'updated' | 'latest'>('idle')

  const handleNavigate = (path: string) => {
    setOpen(false)
    navigate(path)
  }

  const handleCheckUpdate = useCallback(async () => {
    setUpdateStatus('checking')
    try {
      const result = await checkForUpdate()
      setUpdateStatus(result)
      // 'updated' の場合はリロードされるため状態を戻さない
      if (result === 'updated') return
    } catch {
      setUpdateStatus('latest')
    }
    setTimeout(() => setUpdateStatus('idle'), 3000)
  }, [])

  const handleOpenToastHistory = () => {
    setOpen(false)
    setToastHistoryOpen(true)
  }

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button className="p-0 text-muted-foreground hover:text-foreground" aria-label="メニュー">
            <Menu size={24} />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="flex flex-col gap-0 p-0 w-72 sm:max-w-72">
          <SheetHeader className="px-5 pt-5 pb-4 border-b border-border">
            <SheetTitle className="text-base">設定</SheetTitle>
          </SheetHeader>

          {hasFullAccess && (
            <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
              {/* デバッグオーバーレイ切替 */}
              <div className="flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors hover:bg-accent/50">
                <div className="flex items-center gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-emerald-500/15">
                    <Network size={16} className="text-emerald-400" />
                  </div>
                  <span className="text-sm">ネットワーク監視</span>
                </div>
                <Switch checked={showDebugOverlay} onCheckedChange={toggleDebugOverlay} />
              </div>

              {/* ユーザー管理 */}
              <button
                onClick={() => handleNavigate(PATHS.ADMIN.USERS)}
                className="flex w-full items-center justify-between rounded-lg px-3! py-2.5! text-sm transition-colors hover:bg-accent/50"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-blue-500/15">
                    <Users size={16} className="text-blue-400" />
                  </div>
                  <span className="text-sm">ユーザー管理</span>
                </div>
                <ChevronRight size={16} className="text-muted-foreground" />
              </button>

              {/* 連携設定 */}
              <button
                onClick={() => handleNavigate(PATHS.SETTINGS.INTEGRATIONS)}
                className="flex w-full items-center justify-between rounded-lg px-3! py-2.5! text-sm transition-colors hover:bg-accent/50"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-purple-500/15">
                    <Link2 size={16} className="text-purple-400" />
                  </div>
                  <span className="text-sm">連携設定</span>
                </div>
                <ChevronRight size={16} className="text-muted-foreground" />
              </button>

              {/* ストレージ管理 */}
              <button
                onClick={() => handleNavigate(PATHS.STORAGE_MANAGEMENT)}
                className="flex w-full items-center justify-between rounded-lg px-3! py-2.5! text-sm transition-colors hover:bg-accent/50"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-slate-500/15">
                    <HardDrive size={16} className="text-slate-400" />
                  </div>
                  <span className="text-sm">ストレージ管理</span>
                </div>
                <ChevronRight size={16} className="text-muted-foreground" />
              </button>

              {/* インポート */}
              <button
                onClick={() => handleNavigate(PATHS.IMPORT)}
                className="flex w-full items-center justify-between rounded-lg px-3! py-2.5! text-sm transition-colors hover:bg-accent/50"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-green-500/15">
                    <Upload size={16} className="text-green-400" />
                  </div>
                  <span className="text-sm">インポート</span>
                </div>
                <ChevronRight size={16} className="text-muted-foreground" />
              </button>
            </nav>
          )}

          {!hasFullAccess && <div className="flex-1" />}

          <div className="border-t border-border px-3 pt-3">
            <button
              onClick={handleOpenToastHistory}
              className="flex w-full items-center justify-between rounded-lg px-3! py-2.5! text-sm transition-colors hover:bg-accent/50"
            >
              <div className="flex items-center gap-3">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-sky-500/15">
                  <BellRing size={16} className="text-sky-400" />
                </div>
                <span className="text-sm">トースト履歴</span>
              </div>
              <div className="flex items-center gap-2">
                {unreadToastCount > 0 && (
                  <span className="rounded-full bg-sky-500/20 px-2 py-0.5 text-[11px] text-sky-300">
                    {unreadToastCount}
                  </span>
                )}
                <ChevronRight size={16} className="text-muted-foreground" />
              </div>
            </button>
          </div>

          {/* PWA更新チェック */}
          <div className="px-3 py-3">
            <button
              onClick={handleCheckUpdate}
              disabled={updateStatus === 'checking'}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-accent/50 disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-orange-500/15">
                  {updateStatus === 'checking' ? (
                    <Loader2 size={16} className="animate-spin text-orange-400" />
                  ) : (
                    <RefreshCw size={16} className="text-orange-400" />
                  )}
                </div>
                <span className="text-sm">
                  {updateStatus === 'checking' && 'チェック中...'}
                  {updateStatus === 'updated' && '更新を適用中...'}
                  {updateStatus === 'latest' && '最新です'}
                  {updateStatus === 'idle' && 'アップデート確認'}
                </span>
              </div>
            </button>
          </div>

          <div className="border-t border-border px-5 py-3">
            <p className="text-[11px] text-muted-foreground/60">v{__APP_VERSION__}</p>
          </div>
        </SheetContent>
      </Sheet>
      <ToastHistorySheet open={toastHistoryOpen} onOpenChange={setToastHistoryOpen} />
    </>
  )
}
